<template>
  <div class="trucks-page space-y-5">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Trucks</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{{ filteredTrucks.length }} truck{{ filteredTrucks.length !== 1 ? 's' : '' }}</p>
      </div>
      <button @click="openCreateModal" class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
        <span class="material-icons text-[18px]" aria-hidden="true">add</span>
        Add Truck
      </button>
    </div>

    <ErrorState v-if="fetchError && !filteredTrucks.length" title="Couldn't load trucks" :message="fetchError" @retry="fetchTrucks" />
    <template v-else>
    <AlertBanner v-if="fetchError" type="error" message="Couldn't refresh trucks — showing the last loaded list." :dismissible="false" class="mb-4" />
    <DataTable :data="filteredTrucks" :columns="tableColumns" :loading="isLoading" :searchable="true" :pagination="true" loading-text="Loading trucks..." empty-text="No trucks found">
      <template #cell-truckNumber="{ row }">
        <div class="flex items-center gap-2">
          <div class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" :class="row.active !== false ? 'bg-secondary-50 dark:bg-secondary-900/20' : 'bg-neutral-100 dark:bg-neutral-700'">
            <span class="material-icons text-sm" :class="row.active !== false ? 'text-secondary-600 dark:text-secondary-400' : 'text-neutral-400'" aria-hidden="true">local_shipping</span>
          </div>
          <span class="text-sm font-semibold font-mono text-neutral-900 dark:text-neutral-100">{{ row.truckNumber }}</span>
        </div>
      </template>

      <template #cell-active="{ row }">
        <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" :class="row.active !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400'">
          <span class="inline-block w-1.5 h-1.5 rounded-full" :class="row.active !== false ? 'bg-emerald-500' : 'bg-neutral-400'"></span>
          {{ row.active !== false ? 'Active' : 'Inactive' }}
        </span>
      </template>

      <template #actions="{ row }">
        <div class="flex items-center gap-1 justify-center">
          <button @click="openEditModal(row)" class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" title="Edit" aria-label="Edit truck">
            <span class="material-icons text-[16px]" aria-hidden="true">edit</span>
          </button>
          <button @click="toggleActive(row)" class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" :class="row.active !== false ? 'hover:text-amber-600 hover:bg-amber-50 dark:hover:text-amber-400 dark:hover:bg-amber-900/30' : 'hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-emerald-400 dark:hover:bg-emerald-900/30'" :title="row.active !== false ? 'Deactivate' : 'Activate'" :aria-label="row.active !== false ? 'Deactivate truck' : 'Activate truck'">
            <span class="material-icons text-[16px]" aria-hidden="true">{{ row.active !== false ? 'pause_circle' : 'play_circle' }}</span>
          </button>
          <button @click="confirmDeleteTruck(row)" class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" title="Delete" aria-label="Delete truck">
            <span class="material-icons text-[16px]" aria-hidden="true">delete_outline</span>
          </button>
        </div>
      </template>
    </DataTable>
    </template>

    <!-- Create/Edit Modal -->
    <Modal :show="showModal" :title="isEditing ? 'Edit Truck' : 'Add Truck'" size="sm" @close="closeModal">
      <form id="truck-form" @submit.prevent="submitTruck" class="space-y-4">
        <div>
          <label for="trk-number" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Truck Number</label>
          <input id="trk-number" v-model="form.truckNumber" type="text" required :disabled="isEditing" placeholder="e.g. DXB-5521"
            class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50" />
        </div>
        <div v-if="formError" class="flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
          <span class="material-icons text-red-500 text-sm" aria-hidden="true">error</span>
          <p class="text-xs text-red-700 dark:text-red-400">{{ formError }}</p>
        </div>
      </form>
      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button @click="closeModal" class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors">Cancel</button>
          <button type="submit" form="truck-form" :disabled="isSubmitting" class="px-4 py-2 text-sm font-semibold text-white bg-primary-700 rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Create') }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Confirm -->
    <Modal :show="showDeleteConfirm" title="Delete Truck" size="sm" @close="showDeleteConfirm = false">
      <div class="bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/40 rounded-lg p-4 space-y-2">
        <p class="text-sm text-red-700 dark:text-red-300">Delete truck <strong>{{ truckToDelete?.truckNumber }}</strong>?</p>
        <p class="text-xs text-amber-600 dark:text-amber-400">Consider deactivating instead so iCollect validation continues to reference it.</p>
      </div>
      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button @click="showDeleteConfirm = false" class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors">Cancel</button>
          <button @click="deleteTruck" :disabled="isSubmitting" class="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">{{ isSubmitting ? 'Deleting...' : 'Delete' }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import DataTable from "@/components/ui/DataTable.vue";
import Modal from "@/components/ui/Modal.vue";
import ErrorState from "@/components/ui/ErrorState.vue";
import AlertBanner from "@/components/ui/AlertBanner.vue";
import useToast from "@/composables/useToast";

export default {
  name: "TrucksView",
  components: { DataTable, Modal, ErrorState, AlertBanner },
  setup() {
    const store = useStore();
    const apiUrl = store.state.apiUrl;
    store.commit("setCurrentRoute", "Trucks");
    const { showToast } = useToast();

    const filteredTrucks = ref([]);
    const isLoading = ref(false);
    const fetchError = ref(null);
    const showModal = ref(false);
    const showDeleteConfirm = ref(false);
    const isEditing = ref(false);
    const isSubmitting = ref(false);
    const formError = ref(null);
    const editingTruckId = ref(null);
    const truckToDelete = ref(null);
    const form = ref({ truckNumber: "" });

    const tableColumns = [
      { key: "truckNumber", label: "Truck Number", sortable: true },
      { key: "active", label: "Status", sortable: true, align: "center" },
    ];

    const fetchTrucks = async () => {
      isLoading.value = true;
      try {
        const response = await axios.get(`${apiUrl}/trucks/${store.state.currentSite}`);
        filteredTrucks.value = response.data;
        fetchError.value = null;
      } catch (error) {
        console.error("Error fetching trucks:", error);
        fetchError.value = "Failed to load trucks. Please try again.";
      } finally {
        isLoading.value = false;
      }
    };

    const openCreateModal = () => { isEditing.value = false; editingTruckId.value = null; formError.value = null; form.value = { truckNumber: "" }; showModal.value = true; };
    const openEditModal = (t) => { isEditing.value = true; editingTruckId.value = t.id; formError.value = null; form.value = { truckNumber: t.truckNumber }; showModal.value = true; };
    const closeModal = () => { showModal.value = false; formError.value = null; };

    const submitTruck = async () => {
      if (isSubmitting.value) return;
      isSubmitting.value = true; formError.value = null;
      try {
        if (isEditing.value) {
          await axios.put(`${apiUrl}/trucks/${editingTruckId.value}`, { truckNumber: form.value.truckNumber });
        } else {
          await axios.post(`${apiUrl}/trucks`, { truckNumber: form.value.truckNumber, siteId: store.state.currentSite, active: true });
        }
        showToast("success", isEditing.value ? "Truck updated" : "Truck added");
        closeModal(); await fetchTrucks();
      } catch (error) {
        formError.value = error.response?.status === 400 ? (error.response.data.detail || "Validation error") : "An error occurred.";
      } finally { isSubmitting.value = false; }
    };

    const toggleActive = async (t) => {
      // The UI treats a missing `active` field as Active, so compute the new
      // state from the same predicate (legacy rows would otherwise no-op).
      const activating = t.active === false;
      try {
        await axios.put(`${apiUrl}/trucks/${t.id}`, { active: activating });
        showToast("success", activating ? "Truck activated" : "Truck deactivated");
        await fetchTrucks();
      } catch (e) {
        console.error(e);
        showToast("error", `Failed to ${activating ? "activate" : "deactivate"} truck. Please try again.`);
      }
    };
    const confirmDeleteTruck = (t) => { truckToDelete.value = t; showDeleteConfirm.value = true; };
    const deleteTruck = async () => {
      if (!truckToDelete.value) return;
      isSubmitting.value = true;
      try {
        await axios.delete(`${apiUrl}/trucks/${truckToDelete.value.id}`);
        showDeleteConfirm.value = false;
        truckToDelete.value = null;
        showToast("success", "Truck deleted");
        await fetchTrucks();
      } catch (e) {
        console.error(e);
        showToast("error", "Failed to delete truck. Please try again.");
      } finally { isSubmitting.value = false; }
    };

    onMounted(fetchTrucks);
    return { filteredTrucks, isLoading, fetchError, showModal, showDeleteConfirm, isEditing, isSubmitting, formError, form, tableColumns, truckToDelete, openCreateModal, openEditModal, closeModal, submitTruck, toggleActive, confirmDeleteTruck, deleteTruck, fetchTrucks };
  },
};
</script>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
  .trucks-page > * { animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both; }
  .trucks-page > *:nth-child(1) { animation-delay: 0s; }
  .trucks-page > *:nth-child(2) { animation-delay: 0.06s; }
}
@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>
